import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmCrossDockStaging,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProductSelect } from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { useProductOptions } from '@/hooks/useProductOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseCrossDockPage() {
  const { hasAny } = usePermissions();
  const canReceive = hasAny('WAREHOUSE_RECEIVE', 'WAREHOUSE_ADMIN');
  const { getLabel: getProductLabel } = useProductOptions();
  const [staging, setStaging] = useState<WmCrossDockStaging[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');

  const reload = useCallback(() => {
    warehouseApi.listCrossDock().then(setStaging).catch((e) => showApiError(e, 'Failed to load cross-dock'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const stage = async () => {
    if (!warehouseId || !productId) return;
    try {
      await warehouseApi.stageCrossDock({
        warehouseId: Number(warehouseId),
        productId: Number(productId),
        quantity: Number(quantity),
      });
      notify.success('Cross-dock staged');
      reload();
    } catch (e) {
      showApiError(e, 'Stage cross-dock failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Cross-dock staging"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Cross-dock' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Stage inbound → outbound</h2>
        {canReceive ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Select
                label="Warehouse"
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <ProductSelect label="Product *" value={productId} onChange={setProductId} required />
              <Input label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <Button className="mt-3" onClick={() => void stage()}>
              Stage
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Receiving permission required to stage cross-dock.</p>
        )}
      </div>

      <ul className="space-y-2">
        {staging.map((s) => (
          <li key={s.id} className="card flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
            <span>
              WH {s.warehouseId} · {getProductLabel(s.productId, true)} · qty {s.quantity} · {s.statusCode}
            </span>
            {canReceive && s.statusCode === 'STAGED' && (
              <Button
                className="text-sm py-1 px-2"
                onClick={() =>
                  void warehouseApi.completeCrossDock(s.id).then(() => {
                    notify.success('Cross-dock completed');
                    reload();
                  }).catch((e) => showApiError(e, 'Complete cross-dock failed'))
                }
              >
                Complete
              </Button>
            )}
          </li>
        ))}
      </ul>
      {staging.length === 0 && <p className="text-sm text-slate-500">No cross-dock staging records.</p>}
    </div>
  );
}
